import {
    createContext,
    useEffect,
    useMemo,
    useState,
    useContext,
    useRef,
    useCallback,
} from 'react';
import { ERROR_TEXT } from '../constants';
import { arraysAreIdenticalCheck, formatDatePartsToIsoDate } from '../helpers';
import { DEFAULT_TIME_ZONE } from '../timeZones';
import { useAxiosAuthenticated } from '../useAxiosAuthenticated';
import _ from 'lodash';
import { MenuContext } from './MenuContext';
import { reportComponentLink } from '../ReactComponentLink';

export const ReportContext = createContext();
export const ReportProvider = ({ children }) => {
    ///////// Universally needed
    const [errorText, setErrorText] = useState('');
    const { selectedMenuItem } = useContext(MenuContext);
    const [paramCobrand, setParamCobrand] = useState([]);
    const [clientOptions, setClientOptions] = useState([]);
    const [canSelectClient, setCanSelectClient] = useState(false);
    const [paramClient, setParamClient] = useState('all');
    const allUnitsObject = useMemo(() => ({ id: 0, name: 'All Units' }), []);
    const [paramUnit, setParamUnit] = useState(allUnitsObject.id);
    const [paramFromDate, setParamFromDate] = useState(
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000 * 2)
    );
    const [paramToDate, setParamToDate] = useState(new Date());
    const [paramTimeZone, setParamTimeZone] = useState(DEFAULT_TIME_ZONE);

    // The zone the currently loaded reportData was fetched under. Kept separate from
    // paramTimeZone so changing the dropdown doesn't relabel data that's already on screen.
    const [appliedTimeZone, setAppliedTimeZone] = useState(DEFAULT_TIME_ZONE);

    const { get, post, tokenError } = useAxiosAuthenticated();
    useEffect(() => {
        if (tokenError) setErrorText(ERROR_TEXT.token);
    }, [tokenError]);

    ///////// Main Report metadata
    const [reportsMeta, setReportsMeta] = useState({});
    const [inFlight, setInFlight] = useState(false);

    useEffect(() => {
        if (!get) return;
        const makeReportsMetaRequest = async () => {
            try {
                setErrorText('');
                setInFlight(true);
                const url =
                    process.env.REACT_APP_API_BASE_URL + '/reports-meta/all-reports-meta';
                const resp = await get(url);
                let data = resp.data;

                // Filter out any globally disabled reports
                data = data.filter((x) => x.isEnabled);

                // Decorate with links to React components
                data = data.map((x) => ({
                    ...x,
                    pageComponent: reportComponentLink[x.slug],
                    pageType: 'reports',
                }));

                // Convert into a lookup table
                data = _.keyBy(data, 'slug');

                setReportsMeta(data);
                setInFlight(false);
            } catch (error) {
                console.error('Error fetching reports metadata:', error);
                setErrorText(ERROR_TEXT.reportsMeta);
                setInFlight(false);
            }
        };
        makeReportsMetaRequest();
    }, [get]);

    ///////// Selected Report and Report Data
    const [hasMadeFirstRequest, setHasMadeFirstRequest] = useState(false);
    const [selectedReport, _setSelectedReport] = useState();
    const [reportData, setReportData] = useState([]);
    const [noReportsAvailable, setNoReportsAvailable] = useState(false);

    const setSelectedReportBySlug = (slug) => {
        const rep = reportsMeta[slug];
        if (!rep) {
            throw new Error('Invalid report id: ' + slug);
        }

        setHasMadeFirstRequest(false);
        setInFlight(false);
        setErrorText('');
        setReportData([]);

        return _setSelectedReport(rep);
    };

    const generateRequestBody = useCallback(() => {
        return {
            CobrandIds: paramCobrand,
            UnitId: paramUnit || null,
            From: formatDatePartsToIsoDate(paramFromDate),
            To: formatDatePartsToIsoDate(paramToDate),
            TimeZone: paramTimeZone,
        };
    }, [paramCobrand, paramUnit, paramFromDate, paramToDate, paramTimeZone]);

    const generateRequestKey = useCallback(() => {
        return `${selectedReport.slug}_${JSON.stringify(generateRequestBody())}`;
    }, [selectedReport, generateRequestBody]);

    const currentRequestKey = useRef(null);
    useEffect(() => {
        if (!selectedReport) return;

        currentRequestKey.current = generateRequestKey();
    }, [generateRequestKey, selectedReport]);

    const getReportData = async () => {
        try {
            setInFlight(true);
            setErrorText('');
            const preRequestKey = generateRequestKey();
            const url = `${process.env.REACT_APP_API_BASE_URL}/reports/${selectedReport.slug}`;
            const toSend = generateRequestBody();
            const resp = await post(url, toSend);

            // This acts like a page+parameter hash check to make sure nothing has changed
            // since the request started (e.g. user moved to a different report)
            if (preRequestKey !== currentRequestKey.current) return;

            setInFlight(false);
            setReportData(resp.data);
            setAppliedTimeZone(toSend.TimeZone);
            setHasMadeFirstRequest(true);
        } catch (error) {
            console.error('Error fetching report data:', error);
            setErrorText(ERROR_TEXT.reportRequest);
            setInFlight(false);
        }
    };

    ///////// Reports Header
    // Entirety of cobrands available
    const [allValidCobrands, setAllValidCobrands] = useState([]);
    useEffect(() => {
        const makeCobrandsRequest = async () => {
            try {
                setErrorText('');
                const url = process.env.REACT_APP_API_BASE_URL + '/staff/cobrands';
                const resp = await get(url);

                const newCobrandOptions = resp.data.map((x) => ({
                    id: x.cobrandId,
                    name: x.name,
                    clientId: x.clientID,
                    clientName: x.clientName,
                }));

                const uniqueClients = Array.from(
                    newCobrandOptions.reduce((acc, curr) => {
                        if (curr.clientId && !acc.has(curr.clientId)) {
                            acc.set(curr.clientId, {
                                id: curr.clientId,
                                name: curr.clientName,
                            });
                        }
                        return acc;
                    }, new Map())
                ).map(([, value]) => value);

                setClientOptions(uniqueClients);
                setCanSelectClient(uniqueClients.length > 1);
                // If only one client is available, lock selection to it; otherwise allow "all"
                if (uniqueClients.length === 1) {
                    setParamClient(uniqueClients[0].id);
                } else {
                    setParamClient('all');
                }

                setAllValidCobrands(newCobrandOptions);
            } catch (error) {
                console.error(error);
                setErrorText(ERROR_TEXT.cobrands);
            }
        };
        makeCobrandsRequest();
    }, []);

    // OnOff settings
    const [onOffCobrands, setOnOffCobrands] = useState([]);
    useEffect(() => {
        if (!allValidCobrands.length || !post) return;

        const makeOnOffRequest = async () => {
            try {
                setErrorText('');
                const toSend = allValidCobrands.map((x) => x.id);
                const url =
                    process.env.REACT_APP_API_BASE_URL +
                    '/reports-meta/client-console-reports-on-off-batch';
                const resp = await post(url, toSend);
                setOnOffCobrands(resp.data);
            } catch (error) {
                console.error(error);
                setErrorText(ERROR_TEXT.cobrands);
            }
        };
        makeOnOffRequest();
    }, [allValidCobrands, post]);

    // Cobrands available for this report
    const [cobrandOptions, setCobrandOptions] = useState([]);
    useEffect(() => {
        if (!allValidCobrands.length || !selectedReport) return;

        const cobrandIdsWithThisReportOn = onOffCobrands
            .filter((x) => x.reportSlugs.includes(selectedReport.slug))
            .map((x) => x.cobrandId);

        let result = allValidCobrands.filter((x) =>
            cobrandIdsWithThisReportOn.includes(x.id)
        );

        if (paramClient && paramClient !== 'all') {
            result = result.filter((x) => x.clientId === paramClient);
        }

        setNoReportsAvailable(result.length === 0);

        // Update only if the set of cobrand IDs has changed for better UX moving between reports
        setCobrandOptions((prev) => {
            const idsAreEqual = arraysAreIdenticalCheck(prev || [], result, (x) => x.id);
            if (idsAreEqual) return prev;
            setParamCobrand(result?.length ? [result[0].id] : []); // Default param option first valid cobrand
            return result;
        });
    }, [onOffCobrands, allValidCobrands, selectedReport, paramClient]);

    // Units
    const [unitOptions, setUnitOptions] = useState(allUnitsObject);

    useEffect(() => {
        if (!paramCobrand) return;

        if (paramCobrand.length === 0 || paramCobrand.length > 1) {
            setUnitOptions([allUnitsObject]);
            setParamUnit(allUnitsObject.id);
            return;
        }

        const makeUnitsRequest = async () => {
            try {
                setErrorText('');
                const url = `${process.env.REACT_APP_API_BASE_URL}/reports-meta/units?cobrandId=${paramCobrand}`;
                const resp = await get(url);
                setUnitOptions([
                    allUnitsObject,
                    ...resp.data.map((x) => ({ id: x.unitId, name: x.name })),
                ]);
            } catch {
                setErrorText(ERROR_TEXT.cobrands);
            }
        };
        makeUnitsRequest();
    }, [paramCobrand]);

    ///////// Prefetching Logic
    // Track which reports have already been prefetched to avoid duplicate prefetches
    const [prefetchedReports, setPrefetchedReports] = useState(new Set());

    // Check if all header fields are resolved for prefetching
    // Excludes custom reports as they have different parameter requirements
    const areHeaderFieldsResolved = useMemo(() => {
        return (
            allValidCobrands.length > 0 &&
            paramCobrand.length > 0 &&
            unitOptions.length > 0 &&
            paramUnit !== null &&
            paramFromDate &&
            paramToDate &&
            selectedReport &&
            !selectedMenuItem?.isCustom
        );
    }, [
        allValidCobrands,
        paramCobrand,
        unitOptions,
        paramUnit,
        paramFromDate,
        paramToDate,
        selectedReport,
        selectedMenuItem,
    ]);

    // Prefetch data only once when first clicking into a report and all header fields are resolved
    // This primes the server cache so subsequent user requests are faster
    useEffect(() => {
        if (!areHeaderFieldsResolved || !selectedReport) return;

        // Only prefetch if we haven't already prefetched this report
        if (prefetchedReports.has(selectedReport.slug)) return;

        const prefetchReportData = async () => {
            try {
                setPrefetchedReports((prev) => new Set(prev).add(selectedReport.slug));

                const url = `${process.env.REACT_APP_API_BASE_URL}/reports/${selectedReport.slug}`;
                const toSend = generateRequestBody();
                await post(url, toSend);
            } catch (error) {
                // Don't set error state for prefetch failures - this is background work
                console.warn(`Prefetch failed for report: ${selectedReport.slug}`, error);
            }
        };

        prefetchReportData();
    }, [areHeaderFieldsResolved, selectedReport, post, prefetchedReports]);

    ///////// Table filter reset on data change
    // This is to address a bug with the Prime React DataTable where
    // the onValueChange is not always correctly triggered. A reset
    // of the table filters forces this function to re-evaluate.
    const [resetFiltersFunc, setResetFiltersFunc] = useState(_.noop);
    useEffect(() => {
        if (resetFiltersFunc) resetFiltersFunc();
    }, [reportData]);

    ///////// Hidden columns visibility state
    const [showHiddenColumns, setShowHiddenColumns] = useState(false);
    const toggleHiddenColumns = () => {
        setShowHiddenColumns((prev) => !prev);
    };

    return (
        <ReportContext.Provider
            value={{
                // Reports metadata
                reportsMeta,

                // Selected report
                selectedReport,
                setSelectedReportBySlug,
                errorText,

                // Report data
                reportData,
                hasMadeFirstRequest,
                inFlight,
                noReportsAvailable,

                // Header
                cobrandOptions,
                paramCobrand,
                setParamCobrand,

                clientOptions,
                canSelectClient,
                paramClient,
                setParamClient,

                unitOptions,
                paramUnit,
                setParamUnit,

                paramFromDate,
                setParamFromDate,

                paramToDate,
                setParamToDate,

                paramTimeZone,
                setParamTimeZone,
                appliedTimeZone,

                // Run report
                getReportData,

                // Reset Filters
                setResetFiltersFunc,

                // Hidden columns visibility
                showHiddenColumns,
                toggleHiddenColumns,

                // Cobrand OnOff
                onOffCobrands,
            }}
        >
            {children}
        </ReportContext.Provider>
    );
};
